import React from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
} from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import { useThemeMode } from '../contexts/ThemeContext';
import { useScrollReveal } from '../hooks/useScrollReveal';
import bitknoxLogo from '../images/bitknox_black_logo.png';

// CSS keyframes injected once
const scrollKeyframes = `
@keyframes partnerScroll {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
@keyframes partnerScrollReverse {
  0% { transform: translateX(-50%); }
  100% { transform: translateX(0); }
}
`;

// Self-hosted partner logos (served from public/images/partners/)
const partnerLogos = {
  microsoft: '/images/partners/microsoft.svg',
  aws: '/images/partners/aws.svg',
  vaival: '/images/partners/vaival.webp',
  googleCloud: '/images/partners/google-cloud.svg',
  accenture: '/images/partners/accenture.png',
  salesforce: '/images/partners/salesforce.svg',
  aionDigital: '/images/partners/aion-digital.png',
  oracle: '/images/partners/oracle.svg',
  ibm: '/images/partners/ibm.svg',
  sap: '/images/partners/sap.svg',
  adobe: '/images/partners/adobe.svg',
};

function Partners() {
  const { isLight } = useThemeMode();
  const { ref: partnerRef, revealStyle } = useScrollReveal();

  const partnersRow1 = [
    { name: 'Microsoft', logo: partnerLogos.microsoft },
    { name: 'Amazon Web Services', logo: partnerLogos.aws },
    { name: 'Vaival Technologies', logo: partnerLogos.vaival },
    { name: 'Google Cloud', logo: partnerLogos.googleCloud },
    { name: 'Bitknox', logo: bitknoxLogo },
    { name: 'Accenture', logo: partnerLogos.accenture },
  ];

  const partnersRow2 = [
    { name: 'Salesforce', logo: partnerLogos.salesforce },
    { name: 'Aion Digital', logo: partnerLogos.aionDigital },
    { name: 'Oracle', logo: partnerLogos.oracle },
    { name: 'IBM', logo: partnerLogos.ibm },
    { name: 'SAP', logo: partnerLogos.sap },
    { name: 'Adobe', logo: partnerLogos.adobe },
  ];

  const LogoItem = ({ partner }) => (
    <Box
      sx={{
        flex: '0 0 auto',
        width: { xs: 160, sm: 200 },
        height: { xs: 72, sm: 80 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2.5, sm: 3.5 },
        py: 1.5,
        borderRadius: 2.5,
        background: isLight
          ? 'rgba(255, 255, 255, 0.45)'
          : 'rgba(255, 255, 255, 0.04)',
        border: isLight
          ? '1px solid rgba(255, 255, 255, 0.6)'
          : '1px solid rgba(255, 255, 255, 0.08)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'default',
        '&:hover': {
          background: isLight
            ? 'rgba(255, 255, 255, 0.75)'
            : 'rgba(255, 255, 255, 0.08)',
          border: isLight
            ? '1px solid rgba(99, 102, 241, 0.2)'
            : '1px solid rgba(255, 255, 255, 0.15)',
          boxShadow: isLight
            ? '0 8px 24px rgba(99, 102, 241, 0.08)'
            : '0 8px 24px rgba(0, 0, 0, 0.3)',
          transform: 'translateY(-2px)',
          '& img': {
            filter: isLight ? 'grayscale(0%) opacity(1)' : 'brightness(0) invert(1) opacity(1)',
          },
          '& .partner-name': {
            opacity: 1,
          },
        },
      }}
    >
      <Box
        component="img"
        src={partner.logo}
        alt={partner.name}
        sx={{
          maxWidth: '100%',
          maxHeight: { xs: 28, sm: 32 },
          objectFit: 'contain',
          filter: isLight
            ? 'grayscale(100%) opacity(0.5)'
            : 'brightness(0) invert(1) opacity(0.4)',
          transition: 'filter 0.35s ease',
        }}
        onError={(e) => {
          e.target.style.display = 'none';
          if (e.target.nextSibling) e.target.nextSibling.style.display = 'block';
        }}
      />
      <Typography
        sx={{
          display: 'none',
          fontWeight: 700,
          fontSize: '0.8rem',
          color: isLight ? '#6366f1' : '#a5b4fc',
          letterSpacing: '0.5px',
        }}
      >
        {partner.name}
      </Typography>
    </Box>
  );

  const ScrollRow = ({ partners, reverse = false }) => {
    const doubled = [...partners, ...partners];
    return (
      <Box
        sx={{
          overflow: 'hidden',
          position: 'relative',
          maskImage: 'linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)',
        }}
      >
        <Box
          className="partner-scroll-track"
          sx={{
            display: 'flex',
            gap: { xs: 1.5, sm: 2 },
            width: 'max-content',
            animation: `${reverse ? 'partnerScrollReverse' : 'partnerScroll'} ${partners.length * 5}s linear infinite`,
          }}
        >
          {doubled.map((partner, i) => (
            <LogoItem key={`${partner.name}-${i}`} partner={partner} />
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      component="section"
      id="partners"
      sx={{
        py: { xs: 6, md: 8 },
        position: 'relative',
        overflow: 'hidden',
        background: isLight
          ? 'transparent'
          : 'linear-gradient(155deg, #020617 0%, #0f172a 30%, #1e293b 60%, #334155 70%, #0f172a 90%, #020617 100%)',
        backdropFilter: isLight ? 'blur(20px)' : undefined,
        WebkitBackdropFilter: isLight ? 'blur(20px)' : undefined,
      }}
    >
      {/* Inject keyframes */}
      <style>{scrollKeyframes}</style>

      <Container ref={partnerRef} maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 }, ...revealStyle(0) }}>
          <Chip
            icon={<AutoAwesome sx={{ fontSize: '1rem' }} />}
            label="TRUSTED BY INDUSTRY LEADERS"
            sx={{
              mb: 2,
              px: 2,
              background: isLight
                ? 'rgba(255, 255, 255, 0.6)'
                : 'rgba(139, 92, 246, 0.15)',
              border: isLight
                ? '1px solid rgba(255, 255, 255, 0.8)'
                : '1px solid rgba(139, 92, 246, 0.4)',
              borderRadius: '50px',
              color: isLight ? '#1e293b' : '#ffffff',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '1px',
              '& .MuiChip-icon': {
                color: isLight ? '#6366f1' : '#a855f7',
              },
            }}
          />

          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3.2rem' },
              fontWeight: 900,
              lineHeight: 1.1,
              mb: 1.5,
              color: isLight ? '#0f172a' : '#ffffff',
            }}
          >
            Our{' '}
            <Box
              component="span"
              sx={{
                background: isLight
                  ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #818cf8 100%)'
                  : 'linear-gradient(135deg, #38bdf8, #8b5cf6)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Partners
            </Box>
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.95rem', md: '1.1rem' },
              color: isLight
                ? 'rgba(15, 23, 42, 0.65)'
                : 'rgba(248, 250, 252, 0.65)',
              maxWidth: 550,
              mx: 'auto',
            }}
          >
            Collaborating with the world's leading technology companies to
            deliver unparalleled integration solutions.
          </Typography>
        </Box>

        {/* Two-row logo carousel */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: 1.5, sm: 2 },
            '&:hover .partner-scroll-track': {
              animationPlayState: 'paused',
            },
          }}
        >
          <ScrollRow partners={partnersRow1} />
          <ScrollRow partners={partnersRow2} reverse />
        </Box>
      </Container>
    </Box>
  );
}

export default Partners;
